import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const APP_STORE_URL = "https://apps.apple.com/ae/app/credit-stone/id6761537265";

const links = [
  { label: "Index", id: "ecosystem" },
  { label: "Product", id: "showcase" },
  { label: "Services", id: "capital" },
  { label: "Partner", id: "partner" },
  { label: "Clients", id: "audiences" },
  { label: "Story", id: "manifesto" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    setTimeout(() => {
      const el = document.getElementById(id);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 56;
      window.scrollTo({ top, behavior: "smooth" });
    }, 80);
  };

  return (
    <motion.header
      initial={{ y: -16, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1.2, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-700 ${
        scrolled
          ? "bg-[#0A0B0D]/75 backdrop-blur-xl border-b border-white/[0.04]"
          : "bg-transparent"
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-[1700px] mx-auto px-5 sm:px-10 lg:px-12 flex items-center justify-between h-14 sm:h-16">
        {/* Brand: logo fades in when scrolled */}
        <a
          href="#top"
          className="flex items-center min-w-[44px] h-full"
          data-testid="brand-link"
          aria-label="Credit Stone Digital"
        >
          <img
            src="/csd-logo.png"
            alt="Credit Stone Digital"
            className="h-9 sm:h-11 w-auto select-none transition-opacity duration-500 ease-out"
            draggable="false"
            style={{
              filter: "brightness(1.18) contrast(1.05)",
              opacity: scrolled ? 1 : 0,
              transform: scrolled ? "translateY(0)" : "translateY(-4px)",
              transition: "opacity 500ms cubic-bezier(0.16,1,0.3,1), transform 500ms cubic-bezier(0.16,1,0.3,1)",
            }}
          />
        </a>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="link-underline t-mono text-[#7A7A78] hover:text-[#F4F2EC] transition-colors"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2">
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="t-mono px-4 py-2 rounded-full bg-[#F4F2EC] text-[#0A0B0D] hover:bg-white transition-colors"
            data-testid="nav-app-access-btn"
          >
            App access ↗
          </a>
        </div>

        <button
          className="md:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5"
          onClick={() => setOpen(!open)}
          aria-label="menu"
          data-testid="nav-mobile-toggle"
        >
          <span className={`block w-5 h-[1.5px] bg-[#F4F2EC] transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
          <span className={`block w-5 h-[1.5px] bg-[#F4F2EC] transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
        </button>
      </div>

      <motion.div
        initial={false}
        animate={open ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
        className="md:hidden border-t border-white/[0.04] bg-[#0A0B0D]/95 backdrop-blur-xl overflow-hidden"
      >
        <div className="px-6 py-7 flex flex-col gap-5">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className="text-left t-mono text-[#7A7A78] hover:text-[#F4F2EC]"
              data-testid={`mobile-nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setOpen(false)}
            className="mt-2 t-mono px-5 py-3 rounded-full bg-[#F4F2EC] text-[#0A0B0D] self-start"
            data-testid="mobile-app-access-btn"
          >
            App access ↗
          </a>
        </div>
      </motion.div>
    </motion.header>
  );
}
